import { Header, LabelledToggle, Space } from '@procurify/ui'
import styled from 'styled-components'

import { configureColumn as col, DataTable } from '@webapp/components/DataTable'
import { type DatatableColumnConfig } from '@webapp/components/DataTable/types/DataTable.types'
import { type CustomField } from '@webapp/core/entities/internal/CustomField'
import { useProcurifyIntl } from '@webapp/hooks'
import { type POPDFCustomFieldDisplayRow } from '@webapp/modules/purchasing/business-logic/use-cases/getPOPDFCustomFieldDisplay'
import messages from './messages'

// Custom field names render title-cased (Angular parity) without mutating
// the stored name
const TitleCasedNameStyled = styled.span`
  text-transform: capitalize;
`

export interface IPOPDFCustomFieldToggleTableProps {
  title: string
  rows: POPDFCustomFieldDisplayRow[]
  onToggle: (field: CustomField, hidden: boolean) => void
  isSaving: boolean
}

export const POPDFCustomFieldToggleTable = ({
  title,
  rows,
  onToggle,
  isSaving,
}: IPOPDFCustomFieldToggleTableProps) => {
  const intl = useProcurifyIntl()

  const columns: DatatableColumnConfig<'BASIC', POPDFCustomFieldDisplayRow>[] =
    [
      col('BASIC', {
        label: intl.formatMessage(messages.columnCustomField),
        getter: (row) => (
          <TitleCasedNameStyled>{row.field.name}</TitleCasedNameStyled>
        ),
      }),
      col('BASIC', {
        label: intl.formatMessage(messages.columnDisplayInPdf),
        getter: (row) => (
          <LabelledToggle
            checked={row.displayInPDF}
            yesColor='success'
            disabled={isSaving}
            inputProps={{
              'aria-label': intl.formatMessage(messages.toggleAriaLabel, {
                name: row.field.name,
              }),
            }}
            // The new hidden value is the current displayInPDF (shown → hide)
            onChange={() => onToggle(row.field, row.displayInPDF)}
          />
        ),
      }),
    ]

  return (
    <Space size='small'>
      <Header as='h4'>{title}</Header>
      <DataTable<POPDFCustomFieldDisplayRow>
        id={`po-pdf-custom-field-toggle-table-${title}`}
        aria-label={title}
        data={rows}
        columns={columns}
        customizableColumns={false}
        persistCustomizedColumns={false}
        emptyStateText={intl.formatMessage(messages.emptyStateText)}
        emptyStateSize='small'
      />
    </Space>
  )
}

POPDFCustomFieldToggleTable.displayName = 'POPDFCustomFieldToggleTable'
